import React, { useState, forwardRef, ComponentProps, useLayoutEffect, ReactNode } from 'react';
import { css } from '@emotion/react';
import { motion, AnimatePresence } from 'motion/react';
import { Icon, type IconName } from '@frontend/icons';
import { DesktopMiniChatRollupButton } from '@frontend/mini-chat';
import { useMiniChatShallowStore } from '@frontend/mini-chat-store';
import { Breakpoints, breakpoints, useMatchMedia } from '@frontend/responsiveness';
import type { CommonHTMLButtonAttributes } from '@frontend/types';
import { useLocation } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  NakedButton,
  contextFactory,
  IconButton,
  Dot,
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
  NotificationBadge,
  useTooltip,
  SpinningLoader,
} from '@frontend/design-system';
import { useWeaveActionBar } from './store';

interface ActionBarContextProps {
  activeAction: string | null;
  onActionClick: (actionId: string) => void;
  actions: Action[];
}

export const [ActionBarContext, useActionBar] = contextFactory<ActionBarContextProps>();

type Action = {
  label: string;
  onClick: (e: React.MouseEvent) => void;
  hide?: boolean;
  disabled?: boolean;
  hoverLabel?: ReactNode;
  trackingId?: string;
};

interface ActionBarProps {
  children: React.ReactNode;
  getMobileBreakpoint?: (bp: Breakpoints) => number;
  showPopupButton?: boolean;
  actions: Action[];
  isMobileOpen?: boolean;
  setIsMobileOpen?: (isOpen: boolean) => void;
}

export const ActionBar = ({
  children,
  actions,
  getMobileBreakpoint: getCustomMobileBreakpoint,
  showPopupButton = true,
  isMobileOpen: isMobileOpenControl,
  setIsMobileOpen: setIsMobileOpenControl,
}: ActionBarProps) => {
  const getMobileBreakpoint = getCustomMobileBreakpoint ?? ((bp: Breakpoints) => bp.small.max);

  const [activeAction, setActiveAction] = useState<string | null>(null);

  const isMobile = useMatchMedia({ maxWidth: getMobileBreakpoint(breakpoints) });

  const [isMobileOpenInternal, setIsMobileOpenInternal] = useState(false);
  const { chats, errorBoundaryActive: miniChatErrorBoundaryActive } = useMiniChatShallowStore(
    'chats',
    'errorBoundaryActive'
  );
  const miniChatManagementButtonLocation: 'desktop' | 'mobile' | false =
    !!chats.length && (!isMobile ? 'desktop' : 'mobile');
  const location = useLocation();
  const isMessageSendPage = location.pathname.includes('/inbox/new') || !!location.search.personPhone;
  const handleActionClick = (actionId: string) => {
    setActiveAction(actionId === activeAction ? null : actionId);
  };

  const isMobileOpen = typeof isMobileOpenControl === 'boolean' ? isMobileOpenControl : isMobileOpenInternal;
  const setIsMobileOpen =
    typeof setIsMobileOpenControl === 'function' ? setIsMobileOpenControl : setIsMobileOpenInternal;

  return (
    <ActionBarContext.Provider value={{ activeAction, onActionClick: handleActionClick, actions }}>
      <div
        css={[
          css`
            min-width: 57px;
            height: 100%;
            z-index: 1000;
            background: ${theme.colors.white};
            border-top: 1px solid ${theme.colors.neutral20};
            display: flex;
            flex-direction: column;
            align-items: center;
            transition: transform 0.2s;
            gap: ${theme.spacing(1)};
          `,
          isMobile &&
            css`
              position: absolute;
              bottom: 0;
              right: 0;
            `,
          isMobile &&
            isMobileOpen &&
            css`
              transform: translateX(0);
            `,
          isMobile &&
            !isMobileOpen &&
            css`
              transform: translateX(100%);
            `,
        ]}
      >
        <div
          css={[
            css`
              flex-grow: 1;
              width: 100%;
              border-left: 1px solid ${theme.colors.neutral20};
              display: flex;
              flex-direction: column;
              align-items: center;
            `,
            miniChatManagementButtonLocation === 'desktop' &&
              !miniChatErrorBoundaryActive &&
              css`
                border-bottom-left-radius: ${theme.borderRadius.medium};
                border-bottom: 1px solid ${theme.colors.neutral20};
              `,
          ]}
        >
          <PlusActions />
          <div
            css={css`
              margin-top: ${theme.spacing(1.5)};
              width: 100%;
            `}
          >
            {children}
          </div>
          {isMobile && (
            <IconButton
              label='Close Action Bar'
              onClick={() => setIsMobileOpen(false)}
              css={css`
                margin-top: auto;
                margin-bottom: ${theme.spacing(1)};
              `}
            >
              <Icon name='x' size={24} />
            </IconButton>
          )}
        </div>
        {miniChatManagementButtonLocation === 'desktop' && !miniChatErrorBoundaryActive && (
          <DesktopMiniChatRollupButton />
        )}
      </div>
      <AnimatePresence>
        {isMobile && !isMobileOpen && !isMessageSendPage && showPopupButton && (
          <MobileToggle
            onClick={() => setIsMobileOpen(true)}
            css={
              miniChatManagementButtonLocation === 'mobile' && {
                bottom: theme.spacing(6),
              }
            }
          />
        )}
      </AnimatePresence>
    </ActionBarContext.Provider>
  );
};

interface ActionProps extends CommonHTMLButtonAttributes {
  icon: IconName;
  label: string;
  hoverLabel?: ReactNode;
  id: string;
  hasCount?: boolean;
  count?: number;
  trackingId?: string;
  hasDot?: boolean;
  isActive?: boolean;
  status?: 'error' | 'warning' | 'loading';
}

// eslint-disable-next-line react/display-name
const Action = forwardRef<HTMLButtonElement, ActionProps>(
  ({ icon, id, label, hoverLabel, hasCount, hasDot, count, trackingId, isActive, onClick, status, ...rest }, ref) => {
    const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'right-end' });
    const { activeAction, onActionClick } = useActionBar();
    const { setActionsWithDot, removeActionsWithDot } = useWeaveActionBar([
      'setActionsWithDot',
      'removeActionsWithDot',
    ]);
    const isActionActive = isActive ?? activeAction === id;

    useLayoutEffect(() => {
      setActionsWithDot(id, hasDot ?? false);
    }, [hasDot]);

    useLayoutEffect(() => {
      return () => {
        removeActionsWithDot(id);
      };
    }, []);

    const button = (
      <NakedButton
        ref={ref}
        trackingId={trackingId}
        onClick={(e) => {
          onActionClick(id);
          onClick?.(e);
        }}
        css={[
          css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 48px;
            width: 100%;
            color: ${theme.colors.neutral70};
            background-color: ${isActionActive ? theme.colors.neutral10 : theme.colors.white};
            transition: background-color 0.2s, color 0.2s, border 0.2s;
            cursor: pointer;
            :hover {
              background-color: ${theme.colors.neutral5};
              color: ${theme.colors.primary50};
              .notification-dot {
                border: 2px solid ${theme.colors.neutral5};
              }
            }
            .status-indicator {
              top: 6px;
              right: 10px;
              border-radius: 42px;
              position: absolute;
              svg {
                background: ${theme.colors.white};
                border-radius: 42px;
                border: 1px solid ${theme.colors.white};
                box-sizing: border-box;
              }
              .loading-status {
                position: relative;
                top: -4px;
                left: -6px;
                width: 12px;
                height: 12px;
                div {
                  width: 12px;
                  height: 12px;
                }
              }
            }
          `,
          rest.disabled &&
            css`
              color: ${theme.colors.neutral20};
              cursor: not-allowed;
              :hover {
                color: ${theme.colors.neutral20};
                background-color: ${theme.colors.white};
                cursor: not-allowed;
              }
            `,
        ]}
        {...rest}
      >
        <Icon name={icon} size={24} />
        <AnimatePresence>
          {isActionActive && (
            <motion.span
              key={id}
              initial={{ transform: 'translateX(100%)' }}
              animate={{ transform: 'translateX(0)' }}
              exit={{ transform: 'translateX(100%)' }}
              style={{
                position: 'absolute',
                display: 'block',
                zIndex: 1,
                top: 0,
                right: 0,
                width: 4,
                height: '100%',
                backgroundColor: theme.colors.primary50,
              }}
            />
          )}
        </AnimatePresence>
        {!status && hasCount && !!count && (
          <NotificationBadge
            css={{
              position: 'absolute',
              top: `${theme.spacing(1)}`,
              right: theme.spacing(1),
              background: theme.colors.primary50,
            }}
          >
            {count}
          </NotificationBadge>
        )}
        {!status && hasDot && (
          <Dot
            color='critical'
            className='notification-dot'
            css={{
              position: 'absolute',
              right: theme.spacing(1.5),
              top: theme.spacing(1),
              border: '2px solid white',
              height: 12,
              width: 12,
            }}
          />
        )}
        {status && <StatusIndicator status={status} />}
      </NakedButton>
    );

    if (rest.disabled && hoverLabel) {
      return (
        <>
          <span {...triggerProps} ref={triggerProps.ref} css={{ display: 'block', width: '100%' }}>
            {button}
          </span>
          <Tooltip {...tooltipProps}>{hoverLabel}</Tooltip>
        </>
      );
    }

    return (
      <>
        <span {...triggerProps} ref={triggerProps.ref} css={{ display: 'block', width: '100%' }}>
          {button}
        </span>
        <Tooltip {...tooltipProps}>{hoverLabel ?? label}</Tooltip>
      </>
    );
  }
);

const StatusIndicator = ({ status }: { status: 'error' | 'warning' | 'loading' }) => {
  return (
    <span className='status-indicator'>
      {status === 'error' && <Icon name='warning-badge-small' size={16} color={'error'} />}
      {status === 'warning' && <Icon name='warning-badge-small' size={16} color={'warn'} />}
      {status === 'loading' && <SpinningLoader className='loading-status' color='subdued' size='xs' />}
    </span>
  );
};

const MotionNakedButton = motion(NakedButton);

const MobileToggle = (props: ComponentProps<typeof MotionNakedButton>) => {
  return (
    <MotionNakedButton
      initial={{ transform: 'translateY(150%)' }}
      exit={{ transform: 'translateY(150%)' }}
      trackingId='action-bar-btn-mobile-toggle'
      css={css`
        width: 48px;
        height: 48px;
        position: fixed;
        bottom: 4px;
        right: 4px;
        z-index: 1000000;
        border-radius: ${theme.borderRadius.full};
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${theme.colors.primary50};
        color: ${theme.colors.white};
        transition: background-color 0.2s, color 0.2s;
        box-shadow: ${theme.shadows.heavy};
        :hover {
          background-color: ${theme.colors.primary5};
          color: ${theme.colors.primary50};
        }
      `}
      {...props}
    >
      <Icon name='action-bar' size={22} />
    </MotionNakedButton>
  );
};

const PlusActions = () => {
  const { getItemProps, getMenuProps, getTriggerProps } = usePopoverMenu({ placement: 'left-start' });
  const { actions = [] } = useActionBar();

  const visibleActions = actions.filter((action) => !action.hide);

  return (
    <>
      <NakedButton
        {...getTriggerProps()}
        css={css`
          width: 40px;
          height: 40px;
          margin-top: ${theme.spacing(1.5)};
          border-radius: ${theme.borderRadius.small};
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${theme.colors.primary50};
          color: ${theme.colors.white};
          transition: background-color 0.2s, color 0.2s;
          :hover {
            background-color: ${theme.colors.primary5};
            color: ${theme.colors.primary50};
          }
        `}
        trackingId='nav-portal-btn-quick-action'
      >
        <Icon name='plus' size={22} />
      </NakedButton>
      <PopoverMenu {...getMenuProps()}>
        {visibleActions.map((action, index) => (
          <PopoverMenuItem
            key={action.label}
            {...getItemProps({
              index,
              onClick: action.onClick,
              disabled: action.disabled,
            })}
            hoverLabel={action.hoverLabel}
            trackingId={action.trackingId}
          >
            {action.label}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </>
  );
};
ActionBar.Action = Action;
